/**
 * In-app browser for OneDrive and SharePoint files (Microsoft has no drop-in
 * picker that works with our backend-held tokens, so the backend proxies Graph
 * and we render the browser here).
 *
 * Tabs: Recent · Shared with me · OneDrive · SharePoint. SharePoint drills
 * down site → library (item_type 'drive') → folder; each step pushes a
 * breadcrumb crumb carrying the site_id / drive_id / folder_id to query with.
 * Search (q) applies to the current location: sites at the SharePoint root,
 * the library once a drive is open.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Building2, Check, ChevronRight, Clock, Cloud, Loader2, RefreshCw, Search, Users } from 'lucide-react';
import { cloudFilesApi, MicrosoftItem, MicrosoftSource } from '@/services/cloudFilesApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '@/hooks/useUserTimezone';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { cn } from '@/lib/utils';
import { errorDetailText, errorStatus } from '../errorDetail';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { cloudItemsKey } from './useCloudConnect';
import { kindFromMime } from './cloudLinks';
import { FileKindIcon, formatBytes } from './CloudIcons';

function isNavigable(item: MicrosoftItem): boolean {
  return item.is_folder || item.item_type === 'site' || item.item_type === 'drive';
}

interface Crumb {
  label: string;
  site_id?: string | null;
  drive_id?: string | null;
  folder_id?: string | null;
}

interface MicrosoftFileBrowserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Attach the chosen files. Throwing keeps the dialog open with the error shown. */
  onConfirm: (items: MicrosoftItem[]) => Promise<void>;
  /** Reconnect the Microsoft account (token expired / revoked). */
  onReconnect?: () => void;
  accountEmail?: string | null;
  maxSelection?: number;
}

/**
 * 409 = not connected / revoked at Microsoft; 403 = the connected account is
 * outside the org's allowed domains / tenants (reconnect with another one).
 * The backend never sends 401 here.
 */
export function isReconnectError(error: unknown): boolean {
  const s = errorStatus(error);
  return s === 409 || s === 403;
}

export function MicrosoftFileBrowserDialog({
  open,
  onOpenChange,
  onConfirm,
  onReconnect,
  accountEmail,
  maxSelection = 10,
}: MicrosoftFileBrowserDialogProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const orgId = useActiveOrgId();
  const [source, setSource] = useState<MicrosoftSource>('recent');
  const [path, setPath] = useState<Crumb[]>([]);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Record<string, MicrosoftItem>>({});
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSource('recent');
      setPath([]);
      setSearch('');
      setDebounced('');
      setSelected({});
      setAttachError(null);
    }
  }, [open]);

  const here = path[path.length - 1];
  const query = {
    source,
    q: debounced || undefined,
    site_id: here?.site_id ?? null,
    drive_id: here?.drive_id ?? null,
    folder_id: here?.folder_id ?? null,
  };

  const { data: items = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: cloudItemsKey(orgId, query),
    queryFn: () => cloudFilesApi.listMicrosoftItems(query),
    enabled: open,
    staleTime: 30_000,
    retry: false,
  });

  const sorted = useMemo(
    () => [...items].sort((a, b) => Number(isNavigable(b)) - Number(isNavigable(a))),
    [items],
  );

  const tabs: { id: MicrosoftSource; label: string; icon: React.ReactNode }[] = [
    { id: 'recent', label: t('messaging.cloud.msRecent', 'Recent'), icon: <Clock className="h-4 w-4" /> },
    { id: 'shared', label: t('messaging.cloud.msShared', 'Shared with me'), icon: <Users className="h-4 w-4" /> },
    { id: 'onedrive', label: 'OneDrive', icon: <Cloud className="h-4 w-4" /> },
    { id: 'sharepoint', label: 'SharePoint', icon: <Building2 className="h-4 w-4" /> },
  ];

  const switchSource = (next: MicrosoftSource) => {
    setSource(next);
    setPath([]);
  };

  const openFolder = (item: MicrosoftItem) => {
    const type = item.item_type ?? (item.is_folder ? 'folder' : 'file');
    let crumb: Crumb;
    if (type === 'site') {
      crumb = { label: item.name, site_id: item.site_id ?? item.id };
    } else if (type === 'drive') {
      crumb = { label: item.name, site_id: item.site_id ?? here?.site_id ?? null, drive_id: item.drive_id ?? item.id };
    } else {
      crumb = {
        label: item.name,
        site_id: item.site_id ?? here?.site_id ?? null,
        drive_id: item.drive_id ?? here?.drive_id ?? null,
        folder_id: item.id,
      };
    }
    setPath((prev) => [...prev, crumb]);
    setSearch('');
    setDebounced('');
  };

  const toggle = (item: MicrosoftItem) => {
    setSelected((prev) => {
      if (prev[item.id]) {
        const next = { ...prev };
        delete next[item.id];
        return next;
      }
      if (Object.keys(prev).length >= maxSelection) return prev;
      return { ...prev, [item.id]: item };
    });
  };

  const selectedItems = Object.values(selected);
  const reconnect = isReconnectError(error);
  const rootLabel = tabs.find((x) => x.id === source)?.label ?? '';

  const confirm = async () => {
    setAttaching(true);
    setAttachError(null);
    try {
      await onConfirm(selectedItems);
      onOpenChange(false);
    } catch (e) {
      // 403/409 also open the reconnect prompt (useCloudAttach); show the detail here too.
      setAttachError(errorDetailText(e) || t('messaging.cloud.attachFailed', 'Could not attach the selected files.'));
    } finally {
      setAttaching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('messaging.cloud.msTitle', 'Attach from OneDrive & SharePoint')}</DialogTitle>
          <DialogDescription>
            {accountEmail
              ? t('messaging.cloud.signedInAs', 'Signed in as {{email}}', { email: accountEmail })
              : t('messaging.cloud.msHint', 'Files are shared as links. Recipients open them in Microsoft 365.')}
          </DialogDescription>
        </DialogHeader>

        <div role="tablist" className="flex flex-wrap gap-1 border-b pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={source === tab.id}
              onClick={() => switchSource(tab.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm',
                source === tab.id ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('messaging.cloud.searchFiles', 'Search files')}
            className="pl-9"
            aria-label={t('messaging.cloud.searchFiles', 'Search files')}
          />
        </div>

        <nav aria-label={t('messaging.cloud.breadcrumb', 'Folder path')} className="flex flex-wrap items-center gap-1 text-xs">
          <button
            type="button"
            className={cn('hover:underline', path.length === 0 ? 'font-medium' : 'text-primary')}
            onClick={() => setPath([])}
          >
            {rootLabel}
          </button>
          {path.map((crumb, i) => (
            <React.Fragment key={`${crumb.label}-${i}`}>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <button
                type="button"
                className={cn('max-w-[160px] truncate hover:underline', i === path.length - 1 ? 'font-medium' : 'text-primary')}
                onClick={() => setPath((prev) => prev.slice(0, i + 1))}
              >
                {crumb.label}
              </button>
            </React.Fragment>
          ))}
          {isFetching && !isLoading && <Loader2 className="ml-1 h-3 w-3 animate-spin text-muted-foreground" />}
        </nav>

        <div className="max-h-80 min-h-[10rem] space-y-0.5 overflow-y-auto" data-testid="ms-items">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && error && (
            <div className="flex flex-col items-center gap-2 py-6 text-center text-sm" role="alert">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-destructive">
                {errorStatus(error) === 403
                  ? errorDetailText(error) || t('messaging.cloud.errUseAllowedAccount', 'Use an account your organization allows.')
                  : reconnect
                  ? t('messaging.cloud.reconnectNeeded', 'Your {{provider}} connection has expired. Reconnect to continue.', {
                      provider: 'Microsoft',
                    })
                  : errorStatus(error) === 429
                    ? t('messaging.cloud.rateLimited', 'Microsoft is limiting requests right now. Try again in a moment.')
                    : errorDetailText(error) || t('messaging.cloud.loadFailed', 'Could not load files.')}
              </p>
              {reconnect && onReconnect ? (
                <Button size="sm" onClick={onReconnect}>
                  {errorStatus(error) === 403
                    ? t('messaging.cloud.useAnotherAccount', 'Connect a different account')
                    : t('messaging.cloud.reconnect', 'Reconnect')}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  {t('messaging.common.retry', 'Retry')}
                </Button>
              )}
            </div>
          )}
          {!isLoading && !error && sorted.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {debounced
                ? t('messaging.cloud.noMatches', 'No files match "{{q}}".', { q: debounced })
                : t('messaging.cloud.emptyFolder', 'Nothing here yet.')}
            </div>
          )}
          {!isLoading &&
            !error &&
            sorted.map((item) => {
              const isSelected = !!selected[item.id];
              const navigable = isNavigable(item);
              const kind = navigable ? 'folder' : kindFromMime(item.mime_type, item.name);
              const meta = navigable
                ? [item.site_name].filter(Boolean)
                : [item.site_name, formatBytes(item.size), item.modified_at ? fmt.medium(item.modified_at) : null].filter(Boolean);
              return (
                <button
                  key={`${item.drive_id ?? ''}:${item.id}`}
                  type="button"
                  data-testid="ms-item"
                  aria-pressed={navigable ? undefined : isSelected}
                  onClick={() => (navigable ? openFolder(item) : toggle(item))}
                  className={cn(
                    'flex w-full items-center gap-3 rounded px-2 py-2 text-left hover:bg-muted/60',
                    isSelected && 'bg-primary/10',
                  )}
                >
                  <FileKindIcon kind={kind} className="text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{item.name}</div>
                    {meta.length > 0 && <div className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</div>}
                  </div>
                  {navigable ? (
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    isSelected && <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                  )}
                </button>
              );
            })}
        </div>

        {attachError && (
          <p className="text-sm text-destructive" role="alert">
            {attachError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={attaching}>
            {t('messaging.channel.cancel', 'Cancel')}
          </Button>
          <Button disabled={selectedItems.length === 0 || attaching} onClick={confirm}>
            {attaching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('messaging.documents.attachCount', 'Attach ({{n}})', { n: selectedItems.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MicrosoftFileBrowserDialog;
